import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { 
  Users, UserCheck, UserX, Clock, CheckCircle, XCircle, 
  Building2, Mail, Phone, Save, Loader2, ExternalLink, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import { useTenant } from '../../contexts/TenantContext';
import api from '../../utils/api';

const B2BSettings = () => {
  const { tenant, tenantSlug } = useTenant();
  const [settings, setSettings] = useState({
    b2b_portal_enabled: false,
    b2b_auto_approve: false,
    b2b_registration_message: ''
  });
  const [accessRequests, setAccessRequests] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [processDialog, setProcessDialog] = useState(false);
  const [processForm, setProcessForm] = useState({ status: '', rejection_reason: '', default_markup_value: 10 });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, requestsRes, agentsRes] = await Promise.all([
        api.get('/b2b/admin/settings'),
        api.get('/b2b/admin/access-requests'),
        api.get('/b2b/admin/agents')
      ]);
      setSettings(settingsRes.data);
      setAccessRequests(requestsRes.data);
      setAgents(agentsRes.data);
    } catch (error) {
      console.error('Failed to fetch B2B data:', error);
      toast.error('Failed to load B2B settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/b2b/admin/settings', settings);
      toast.success('B2B settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const openProcessDialog = (request, status) => {
    setSelectedRequest(request);
    setProcessForm({ status, rejection_reason: '', default_markup_value: 10 });
    setProcessDialog(true);
  };

  const handleProcessRequest = async () => {
    if (!selectedRequest) return;
    
    setProcessing(true);
    try {
      await api.put(`/b2b/admin/access-requests/${selectedRequest.request_id}`, processForm);
      toast.success(`Request ${processForm.status.toLowerCase()}`);
      setProcessDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    } finally {
      setProcessing(false);
    }
  };

  const handleToggleAgent = async (agentId) => {
    try {
      await api.put(`/b2b/admin/agents/${agentId}/toggle`);
      toast.success('Agent status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update agent status');
    }
  };

  const copyPortalUrl = () => {
    const url = `${window.location.origin}/${tenantSlug}/partner`;
    navigator.clipboard.writeText(url);
    toast.success('Portal URL copied to clipboard');
  };

  const pendingRequests = accessRequests.filter(r => r.status === 'PENDING');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Settings Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                B2B Partner Portal Settings
              </CardTitle>
              <CardDescription>Configure B2B agent access to your portal</CardDescription>
            </div>
            <Button onClick={handleSaveSettings} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Settings
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable Portal */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <Label className="text-base font-medium">Enable B2B Partner Portal</Label>
              <p className="text-sm text-gray-500">Allow B2B agents to register and access your portal</p>
            </div>
            <Switch
              checked={settings.b2b_portal_enabled}
              onCheckedChange={(v) => setSettings(prev => ({ ...prev, b2b_portal_enabled: v }))}
            />
          </div>

          {settings.b2b_portal_enabled && (
            <>
              {/* Portal URL */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <Label className="text-sm font-medium text-blue-900">Your B2B Portal URL</Label>
                <div className="flex items-center gap-2 mt-2">
                  <code className="flex-1 px-3 py-2 bg-white rounded border text-sm">
                    {window.location.origin}/{tenantSlug}/partner
                  </code>
                  <Button variant="outline" size="sm" onClick={copyPortalUrl}>
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => window.open(`/${tenantSlug}/partner`, '_blank')}>
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Open
                  </Button>
                </div>
              </div>

              {/* Auto Approve */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-base font-medium">Auto-Approve Registrations</Label>
                  <p className="text-sm text-gray-500">Automatically approve new B2B agent registrations</p>
                </div>
                <Switch
                  checked={settings.b2b_auto_approve}
                  onCheckedChange={(v) => setSettings(prev => ({ ...prev, b2b_auto_approve: v }))}
                />
              </div>

              {/* Registration Message */}
              <div className="space-y-2">
                <Label>Custom Registration Message</Label>
                <textarea
                  placeholder="Welcome message shown on the registration page..."
                  className="w-full px-3 py-2 border rounded-md text-sm"
                  rows={3}
                  value={settings.b2b_registration_message || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, b2b_registration_message: e.target.value }))}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Pending Requests */}
      {pendingRequests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Pending Access Requests
              <Badge className="bg-amber-100 text-amber-700">{pendingRequests.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingRequests.map((request) => (
                  <TableRow key={request.request_id}>
                    <TableCell>
                      <Badge variant={request.request_type === 'NEW_REGISTRATION' ? 'default' : 'secondary'}>
                        {request.request_type === 'NEW_REGISTRATION' ? 'New' : 'Cross-Portal'}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">{request.company_name || '-'}</TableCell>
                    <TableCell>{request.contact_person}</TableCell>
                    <TableCell>{request.email}</TableCell>
                    <TableCell>{new Date(request.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="mr-2 text-green-600 border-green-200 hover:bg-green-50"
                        onClick={() => openProcessDialog(request, 'APPROVED')}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => openProcessDialog(request, 'REJECTED')}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Active B2B Agents */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-green-500" />
            B2B Agents
            <Badge variant="secondary">{agents.length}</Badge>
          </CardTitle>
          <CardDescription>Manage your B2B partner agents</CardDescription>
        </CardHeader>
        <CardContent>
          {agents.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No B2B agents yet</p>
              <p className="text-sm">Enable the B2B portal to start receiving registrations</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone / WhatsApp</TableHead>
                  <TableHead>Markup</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {agents.map((agent) => (
                  <TableRow key={agent.user_id}>
                    <TableCell className="font-medium">{agent.company_name || '-'}</TableCell>
                    <TableCell>{agent.full_name}</TableCell>
                    <TableCell>{agent.email}</TableCell>
                    <TableCell>{agent.phone || '-'}</TableCell>
                    <TableCell>{agent.default_markup_value}%</TableCell>
                    <TableCell>
                      <Badge variant={agent.is_active ? 'default' : 'secondary'}>
                        {agent.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleToggleAgent(agent.user_id)}
                      >
                        {agent.is_active ? (
                          <><UserX className="w-4 h-4 mr-1" /> Disable</>
                        ) : (
                          <><UserCheck className="w-4 h-4 mr-1" /> Enable</>
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Process Dialog */}
      <Dialog open={processDialog} onOpenChange={setProcessDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {processForm.status === 'APPROVED' ? 'Approve Request' : 'Reject Request'}
            </DialogTitle>
            <DialogDescription>
              {selectedRequest?.company_name} - {selectedRequest?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {processForm.status === 'APPROVED' ? (
              <div className="space-y-2">
                <Label>Default Markup (%)</Label>
                <Input
                  type="number"
                  value={processForm.default_markup_value}
                  onChange={(e) => setProcessForm(prev => ({ ...prev, default_markup_value: parseFloat(e.target.value) || 0 }))}
                  placeholder="10"
                />
                <p className="text-sm text-gray-500">The agent can adjust this later for individual quotations</p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Rejection Reason</Label>
                <textarea
                  className="w-full px-3 py-2 border rounded-md text-sm"
                  rows={3}
                  placeholder="Optional: Explain why the request is being rejected..."
                  value={processForm.rejection_reason}
                  onChange={(e) => setProcessForm(prev => ({ ...prev, rejection_reason: e.target.value }))}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProcessDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleProcessRequest}
              disabled={processing}
              className={processForm.status === 'APPROVED' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {processForm.status === 'APPROVED' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default B2BSettings;
